<script>
import { mapActions, mapGetters } from 'vuex'
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'

export default {
  name: 'ManagementCertificatesEdit',
  components: {
    Action: () => import('@/components/general/Action'),
    ActionBar: () => import('@/components/general/ActionBar'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    Dropdown: () => import('@/components/general/Dropdown'),
    DropdownLink: () => import('@/components/general/DropdownLink'),
    FormSection: () => import('@/components/general/FormSection'),
    InputField: () => import('@/components/general/InputField'),
    Modal: () => import('@/components/general/Modal'),
    ModalConfirm: () => import('@/components/macro/ModalConfirm'),
    Upload: () => import('@/components/general/Upload'),
    RichTextEditor: () => import('@/components/general/RichTextEditor'),
    Radio: () => import('@/components/general/Radio'),
    Tabs: () => import('@/components/general/Tabs'),
    Accordion: () => import('@/components/general/Accordion'),
    AccordionItem: () => import('@/components/general/AccordionItem'),
    FilterList: () => import('@/components/general/FilterList'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    Pagination: () => import('@/components/general/Pagination')
  },

  data () {
    return {
      backUrl: { name: 'management.certificates' },
      activeTab: 'default',
      certificateImagesType: 'F',
      formData: {
        id: null,
        name: null,
        html: null,
        imageFront: null,
        imageBack: null
      },

      // Tabs configuration
      tabLinks: [
        {
          text: 'management.certificate:tab.default',
          alias: 'default'
        },
        {
          text: 'management.certificate:tab.thematic',
          alias: 'thematic'
        }
      ],

      backAndFrontOptions: [
        {
          label: this.$t('global:image.front'),
          value: 'F'
        },
        {
          label: this.$t('global:image.back.and.front'),
          value: 'FV'
        }
      ],

      tagInfoList: [
        {
          text: this.$t('global:form.management.certificate.edit:tag.info.student'),
          value: 'student'
        },
        {
          text: this.$t('global:form.management.certificate.edit:tag.info.solution.title'),
          value: 'solutionTitle'
        },
        {
          text: this.$t('global:form.management.certificate.edit:tag.info.solution.description'),
          value: 'solutionDescription'
        },
        {
          text: this.$t('global:form.management.certificate.edit:tag.info.workload'),
          value: 'workload'
        },
        {
          text: this.$t('global:form.management.certificate.edit:tag.info.initial'),
          value: 'initial'
        },
        {
          text: this.$t('global:form.management.certificate.edit:tag.info.conclusion'),
          value: 'conclusion'
        },
        {
          text: this.$t('global:form.management.certificate.edit:tag.info.session'),
          value: 'session'
        },
        {
          text: this.$t('global:form.management.certificate.edit:tag.info.url_hash'),
          value: 'url_hash'
        }
      ],

      modalActive: false,
      formChanged: false,
      loggedUserId: null,

      // Thematic pages data
      thematicPages: [],
      customizations: [],

      // Search and pagination for thematic pages
      thematicPagesSearch: '',
      thematicPagesPagination: {
        page: 1,
        lastPage: null
      },

      // Customization modal state
      showCustomizationForm: false,
      customizationImagesType: 'F',
      selectedThematicPage: null,
      selectedContent: null,
      selectedContentType: 'solution',
      isEditingCustomization: false,
      selectedCustomizationId: null,

      customizationFormData: {
        name: null,
        html: null,
        imageFront: null,
        imageBack: null
      },

      // Modal de confirmação para exclusão de personalização
      showDeleteConfirmModal: false,
      deleteConfirmData: {
        thematicPage: null,
        content: null,
        contentType: null
      },

      // Modal de aviso para configurar página com conteúdos personalizados
      showPageConfigWarningModal: false,
      pageConfigWarningData: {
        thematicPage: null
      },

      // Flag para indicar que devemos remover customizações individuais após salvar bulk
      pendingRemoveIndividualCustomizations: false
    }
  },

  validations: {
    formData: {
      name: {
        required,
        maxLength: maxLength(100)
      },

      html: { required },
      imageFront: { required },
      imageBack: {
        required: requiredIf(function () {
          return this.certificateImagesType === 'FV'
        })
      }
    },

    customizationFormData: {
      name: {
        required,
        maxLength: maxLength(100)
      },

      html: { required },
      imageFront: { required },
      imageBack: {
        required: requiredIf(function () {
          return this.customizationImagesType === 'FV'
        })
      }
    }
  },

  computed: {
    ...mapGetters({
      loggedUserData: 'getUserData'
    }),

    currentUserId () {
      if (this.loggedUserId) {
        return this.loggedUserId
      }

      if (this.loggedUserData) {
        if (this.loggedUserData.id) {
          return this.loggedUserData.id
        }

        if (this.loggedUserData.user && this.loggedUserData.user.id) {
          return this.loggedUserData.user.id
        }
      }

      return null
    },

    isEditing () {
      return this.$route.params.id && this.$route.params.isNew !== 'new'
    },

    customizationModalTitle () {
      if (!this.selectedContent) return ''
      const contentName = this.selectedContent.name || this.selectedContent.title || ''

      return `${this.$t('management.certificate:customization.modal.title')} - ${contentName}`
    },

    customizationSubtitle () {
      if (!this.selectedContent) return ''
      const contentName = this.selectedContent.name || this.selectedContent.title || ''

      return this.$t('management.certificate:customization.modal.subtitle', { name: contentName })
    },

    // Páginas temáticas filtradas pelo termo de busca
    filteredThematicPages () {
      if (!this.thematicPagesSearch) {
        return this.thematicPages
      }

      const searchLower = this.thematicPagesSearch.toLowerCase()

      return this.thematicPages.filter(page =>
        page.name.toLowerCase().includes(searchLower)
      )
    },

    // Páginas temáticas que TÊM conteúdos vinculados (exibir em accordion)
    thematicPagesWithSolutions () {
      return this.filteredThematicPages.filter(page => this.hasThematicPageContents(page))
    },

    // Páginas temáticas que NÃO têm conteúdos vinculados (exibir como item simples)
    thematicPagesWithoutSolutions () {
      return this.filteredThematicPages.filter(page => !this.hasThematicPageContents(page))
    },

    // Header title dinâmico baseado no contexto
    headerTitle () {
      // 1. Se estiver configurando um conteúdo (solução, trilha, programa, ferramenta)
      if (this.showCustomizationForm && this.selectedContent && this.selectedContentType !== 'thematic_page') {
        return this.selectedContent.name || this.selectedContent.title || ''
      }

      // 2. Se estiver configurando uma Página Temática
      if (this.showCustomizationForm && this.selectedThematicPage && this.selectedContentType === 'thematic_page') {
        return this.selectedThematicPage.name || ''
      }

      // 3. Caso contrário, exibir o nome do Certificado Padrão
      return this.formData.name || this.$t('management.certificate.edit:header.title.main')
    }
  },

  watch: {
    'certificateImagesType' () {
      this.$v.formData.imageBack.$reset()
    },

    'customizationImagesType' () {
      this.$v.customizationFormData.imageBack.$reset()
    },

    'formData.html' () {
      if (this.$v.formData.html.$dirty) {
        this.formChanged = true
      }
    },

    'formData.imageFront' () {
      if (this.$v.formData.imageFront.$dirty) {
        this.formChanged = true
      }
    },

    'formData.imageBack' () {
      if (this.$v.formData.imageBack.$dirty) {
        this.formChanged = true
      }
    }
  },

  created () {
    this.ensureLoggedUser()

    if (this.$route.params.id) {
      this.setFetching(true)

      Promise.all([
        this.attemptGetCertificateManagement(this.$route.params.id),
        this.loadThematicPages()
      ]).then(([ certificateData ]) => {
        this.formData = {
          id: certificateData.id,
          name: certificateData.name,
          html: certificateData.template,
          imageFront: certificateData.imageFront,
          imageBack: certificateData.imageBack
        }

        this.certificateImagesType = (!!certificateData.imageFront && !!certificateData.imageBack) ? 'FV' : 'F'

        // Load customizations if available - mapear do formato do backend para o formato do frontend
        if (certificateData.certificateCustomizations) {
          this.customizations = this.mapCustomizationsFromBackend(certificateData.certificateCustomizations)
        }
      }).finally(() => {
        this.setFetching(false)
      })
    } else {
      this.loadThematicPages()
    }
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptSaveCertificate',
      'attemptGetCertificateManagement',
      'attemptUpdateCertificate',
      'attemptGetPreviewCertificate',
      'attemptSaveCertificateCustomization',
      'attemptSaveCertificateCustomizationBulk',
      'attemptUpdateCertificateCustomization',
      'attemptDeleteCertificateCustomization',
      'attemptGetThematicPagesList',
      'attemptGetLoggedUser'
    ]),

    onTabChange (alias) {
      if (this.showCustomizationForm) {
        this.closeCustomizationForm()
      }

      this.activeTab = alias
    },

    leave () {
      this.modalConfirm = false

      this.$nextTick(() => {
        this.$router.push(this.backUrl)
      })
    },

    async loadThematicPages () {
      this.setFetching(true)

      try {
        const params = {
          page: this.thematicPagesPagination.page,
          filter: {
            active: 1,
            has_content: true
          },

          order: { name: 'asc' }
        }

        if (this.thematicPagesSearch) {
          params.query = { name: this.thematicPagesSearch }
        }

        const response = await this.attemptGetThematicPagesList(params)

        this.thematicPages = response.data || []
        this.thematicPagesPagination.lastPage = response.lastPage || 1
      } catch (error) {
        console.error('Error loading thematic pages:', error)
      } finally {
        this.setFetching(false)
      }
    },

    searchThematicPages (search) {
      this.thematicPagesSearch = search
      this.thematicPagesPagination.page = 1
      this.loadThematicPages()
    },

    prevThematicPage () {
      if (this.thematicPagesPagination.page > 1) {
        this.thematicPagesPagination.page -= 1
        this.loadThematicPages()
      }
    },

    nextThematicPage () {
      if (this.thematicPagesPagination.page < this.thematicPagesPagination.lastPage) {
        this.thematicPagesPagination.page += 1
        this.loadThematicPages()
      }
    },

    firstThematicPage () {
      this.thematicPagesPagination.page = 1
      this.loadThematicPages()
    },

    lastThematicPage () {
      this.thematicPagesPagination.page = this.thematicPagesPagination.lastPage
      this.loadThematicPages()
    },

    submitCreation () {
      this.setFetching(true)

      this.attemptSaveCertificate(this.formatFormData()).then((response) => {
        this.setFeedback({ message: this.$t('management.certificate:feedback.created.success') })

        // Redirecionar para a rota de edição com o ID do certificado criado
        // Isso habilita a aba de personalizações
        const certificateId = response.id || response.data?.id

        if (certificateId) {
          this.$router.replace({
            name: 'management.certificates.edit',
            params: { id: certificateId }
          })
        }
      }).catch(() => {
        this.setFeedback({ message: this.$t('management.certificate:feedback.created.error') })
      }).finally(() => {
        this.setFetching(false)
      })
    },

    submitUpdate () {
      this.setFetching(true)

      this.attemptUpdateCertificate(this.formatFormData()).then(() => {
        this.setFeedback({ message: this.$t('management.certificate:feedback.updated.success') })
        this.formChanged = false
      }).catch(() => {
        this.setFeedback({ message: this.$t('management.certificate:feedback.updated.error') })
      }).finally(() => {
        this.setFetching(false)
      })
    },

    submit (next) {
      this.$v.formData.$touch()

      if (!this.$v.formData.$invalid) {
        this.$route.params.id ? this.submitUpdate(next) : this.submitCreation(next)
      } else {
        this.setFeedback({ message: this.$t('management.certificate:feedback.validation.error') })
      }
    },

    formatFormData () {
      const data = {}

      if (this.$route.params.id) {
        data.id = this.$route.params.id
      }

      data.name = this.formData.name
      data.template = this.formData.html
      data.pages = this.certificateImagesType
      data.image_front = this.formData.imageFront
      data.image_back = this.certificateImagesType === 'FV' ? this.formData.imageBack : null

      return data
    },

    preview () {
      this.setFetching(true)

      if (this.$route.params.id) {
        this.attemptGetPreviewCertificate(this.$route.params.id).then((response) => {
          const blob = new Blob([ response.data ], { type: 'application/pdf' })
          const link = document.createElement('a')

          link.href = window.URL.createObjectURL(blob)
          link.download = 'Certificado.pdf'
          link.click()
        }).catch(() => {
          this.setFeedback({ message: this.$t('global:form.management.certificate.preview.error') })
        }).finally(() => {
          this.setFetching(false)
        })
      }
    },

    addTagToHTML (tag, isCustomization = false) {
      const targetData = isCustomization ? this.customizationFormData : this.formData

      if (targetData.html === null) {
        targetData.html = `<p>{{ ${tag} }}</p>`
      } else {
        targetData.html = targetData.html.substring(0, targetData.html.length - 4) + `{{${tag}}}</p>`
      }
    },

    // Helper methods para conteúdos da página temática
    hasThematicPageContents (page) {
      return (
        (page.thematicPageSolutions && page.thematicPageSolutions.length > 0) ||
        (page.thematicPageLearningPaths && page.thematicPageLearningPaths.length > 0) ||
        (page.thematicPagePrograms && page.thematicPagePrograms.length > 0) ||
        (page.thematicPageTools && page.thematicPageTools.length > 0)
      )
    },

    getThematicPageContents (page) {
      const contents = []

      // Soluções
      if (page.thematicPageSolutions && page.thematicPageSolutions.length > 0) {
        page.thematicPageSolutions.forEach(item => {
          contents.push({
            id: item.solution ? item.solution.id : item.id,
            name: item.solution ? item.solution.name : item.name,
            type: 'solution',
            typeLabel: this.$t('management.certificate:content.type.solution')
          })
        })
      }

      // Trilhas
      if (page.thematicPageLearningPaths && page.thematicPageLearningPaths.length > 0) {
        page.thematicPageLearningPaths.forEach(item => {
          contents.push({
            id: item.learningPath ? item.learningPath.id : item.id,
            name: item.learningPath ? item.learningPath.name : item.name,
            type: 'learning_path',
            typeLabel: this.$t('management.certificate:content.type.learning_path')
          })
        })
      }

      // Programas
      if (page.thematicPagePrograms && page.thematicPagePrograms.length > 0) {
        page.thematicPagePrograms.forEach(item => {
          contents.push({
            id: item.program ? item.program.id : item.id,
            name: item.program ? item.program.name : item.name,
            type: 'program',
            typeLabel: this.$t('management.certificate:content.type.program')
          })
        })
      }

      // Ferramentas
      if (page.thematicPageTools && page.thematicPageTools.length > 0) {
        page.thematicPageTools.forEach(item => {
          contents.push({
            id: item.tool ? item.tool.id : item.id,
            name: item.tool ? item.tool.name : item.name,
            type: 'tool',
            typeLabel: this.$t('management.certificate:content.type.tool')
          })
        })
      }

      return contents
    },

    mapCustomizationsFromBackend (backendCustomizations) {
      return backendCustomizations.map(c => {
        let contentType = null
        let contentId = null

        if (c.solution) {
          contentType = 'solution'
          contentId = c.solution.id
        } else if (c.learningPath) {
          contentType = 'learning_path'
          contentId = c.learningPath.id
        } else if (c.program) {
          contentType = 'program'
          contentId = c.program.id
        } else if (c.tool) {
          contentType = 'tool'
          contentId = c.tool.id
        } else if (c.thematicPage) {
          // Customização da página temática em si
          contentType = 'thematic_page'
          contentId = c.thematicPage.id
        }

        return {
          id: c.id,
          thematicPageId: c.thematicPage ? c.thematicPage.id : null,
          contentId: contentId,
          contentType: contentType,
          name: c.name,
          template: c.template,
          imageFront: c.imageFront,
          imageBack: c.imageBack,
          isCustomizedByThematicPage: c.customizedByThematicPage || false
        }
      })
    },

    // Customization methods
    // Verifica se um conteúdo específico (solução, trilha, programa, ferramenta) está configurado
    isContentConfigured (thematicPageId, contentId, contentType) {
      return this.customizations.some(c =>
        c.thematicPageId === thematicPageId &&
        c.contentId === contentId &&
        c.contentType === contentType
      )
    },

    isThematicPageConfigured (thematicPageId) {
      return this.customizations.some(c =>
        c.thematicPageId === thematicPageId &&
        c.isCustomizedByThematicPage === true
      )
    },

    getCustomization (thematicPageId, contentId, contentType) {
      return this.customizations.find(c =>
        c.thematicPageId === thematicPageId &&
        c.contentId === contentId &&
        c.contentType === contentType
      )
    },

    hasConfiguredContents (thematicPage) {
      const contents = this.getThematicPageContents(thematicPage)

      return contents.some(content =>
        this.isContentConfigured(thematicPage.id, content.id, content.type)
      )
    },

    handleConfigurePage (thematicPage) {
      if (this.isThematicPageConfigured(thematicPage.id)) {
        this.openCustomizationForm(thematicPage, thematicPage, 'thematic_page', true)

        return
      }

      if (this.hasConfiguredContents(thematicPage)) {
        this.pageConfigWarningData.thematicPage = thematicPage
        this.showPageConfigWarningModal = true

        return
      }

      this.openCustomizationForm(thematicPage, thematicPage, 'thematic_page', false)
    },

    confirmPageConfiguration () {
      const thematicPage = this.pageConfigWarningData.thematicPage

      this.pendingRemoveIndividualCustomizations = true

      this.showPageConfigWarningModal = false
      this.openCustomizationForm(thematicPage, thematicPage, 'thematic_page', false)
    },

    cancelPageConfiguration () {
      this.showPageConfigWarningModal = false
      this.pageConfigWarningData.thematicPage = null
    },

    confirmDeleteCustomization (thematicPage, content, contentType) {
      this.deleteConfirmData = {
        thematicPage,
        content,
        contentType
      }

      this.showDeleteConfirmModal = true
    },

    executeDeleteCustomization () {
      const { thematicPage, content, contentType } = this.deleteConfirmData

      this.showDeleteConfirmModal = false
      this.deleteCustomization(thematicPage, content, contentType)

      this.deleteConfirmData = {
        thematicPage: null,
        content: null,
        contentType: null
      }
    },

    cancelDeleteCustomization () {
      this.showDeleteConfirmModal = false

      this.deleteConfirmData = {
        thematicPage: null,
        content: null,
        contentType: null
      }
    },

    openCustomizationForm (thematicPage, content, contentType, isEditing = false) {
      this.selectedThematicPage = thematicPage
      this.selectedContent = content
      this.selectedContentType = contentType
      this.isEditingCustomization = isEditing

      if (isEditing) {
        let customization = null

        if (contentType === 'thematic_page') {
          customization = this.customizations.find(c =>
            c.thematicPageId === thematicPage.id &&
            c.isCustomizedByThematicPage === true
          )
        } else {
          customization = this.getCustomization(thematicPage.id, content.id, contentType)
        }

        if (customization) {
          this.selectedCustomizationId = customization.id

          this.customizationFormData = {
            name: customization.name,
            html: customization.template,
            imageFront: customization.imageFront,
            imageBack: customization.imageBack
          }

          this.customizationImagesType = (!!customization.imageFront && !!customization.imageBack) ? 'FV' : 'F'
        }
      } else {
        this.selectedCustomizationId = null
        this.resetCustomizationForm()
      }

      this.showCustomizationForm = true
    },

    resetCustomizationForm () {
      this.customizationFormData = {
        name: null,
        html: null,
        imageFront: null,
        imageBack: null
      }

      this.customizationImagesType = 'F'
      this.$v.customizationFormData.$reset()
    },

    closeCustomizationForm () {
      this.showCustomizationForm = false
      this.selectedContent = null
      this.selectedContentType = 'solution'
      this.isEditingCustomization = false
      this.selectedCustomizationId = null
      this.pendingRemoveIndividualCustomizations = false
      this.resetCustomizationForm()
    },

    async ensureLoggedUser () {
      if (this.currentUserId) {
        return
      }

      try {
        const response = await this.attemptGetLoggedUser()

        if (response && response.user && response.user.id) {
          this.loggedUserId = response.user.id
        }
      } catch (error) {
        console.error('Error fetching logged user data for certificate customization:', error)
      }
    },

    formatBulkCustomizationPayload (contents) {
      // Nova estrutura: certificate_info (dados do certificado) + targets (array de conteúdos)
      const certificateInfo = {
        certificate_id: this.formData.id,
        name: this.customizationFormData.name,
        template: this.customizationFormData.html,
        pages: this.customizationImagesType,
        thematic_page_id: this.selectedThematicPage?.id || null,
        is_customized_by_thematic_page: true,
        // Imagens são enviadas uma única vez no certificate_info
        image_front: this.customizationFormData.imageFront,
        image_back: this.customizationImagesType === 'FV' ? this.customizationFormData.imageBack : null
      }

      // Targets contém apenas content_id e content_type
      const targets = contents.map(content => ({
        content_id: content.id,
        content_type: content.type
      }))

      return {
        certificateInfo,
        targets
      }
    },

    async refreshCertificateCustomizations () {
      if (!this.formData.id) {
        return
      }

      try {
        const certificateData = await this.attemptGetCertificateManagement(this.formData.id)

        if (certificateData?.certificateCustomizations) {
          this.customizations = this.mapCustomizationsFromBackend(certificateData.certificateCustomizations)
        }
      } catch (error) {
        console.error('Error refreshing certificate customizations:', error)
      }
    },

    formatCustomizationPayload () {
      // Determina se a configuração é feita via página temática
      // Se o contentType for 'thematic_page', significa que estamos configurando via página
      // Caso contrário, é configuração individual de conteúdo
      const isCustomizedByThematicPage = this.selectedContentType === 'thematic_page'

      return {
        certificate_id: this.formData.id,
        thematic_page_id: this.selectedThematicPage.id,
        content_id: this.selectedContent.id,
        content_type: this.selectedContentType,
        name: this.customizationFormData.name,
        template: this.customizationFormData.html,
        pages: this.customizationImagesType,
        image_front: this.customizationFormData.imageFront,
        image_back: this.customizationImagesType === 'FV' ? this.customizationFormData.imageBack : null,
        is_customized_by_thematic_page: isCustomizedByThematicPage
      }
    },

    async saveCustomization () {
      this.$v.customizationFormData.$touch()

      if (this.$v.customizationFormData.$invalid) {
        this.setFeedback({ message: this.$t('management.certificate:feedback.validation.error') })

        return
      }

      this.setFetching(true)

      try {
        const isBulkThematic = this.selectedContentType === 'thematic_page'

        if (isBulkThematic) {
          if (!this.selectedThematicPage) {
            this.setFeedback({ message: this.$t('management.certificate:customization.bulk.error') })

            return
          }

          const contents = this.getThematicPageContents(this.selectedThematicPage)

          if (!contents.length) {
            this.setFeedback({ message: this.$t('management.certificate:customization.bulk.empty') })

            return
          }

          await this.ensureLoggedUser()

          if (!this.currentUserId) {
            this.setFeedback({ message: this.$t('management.certificate:customization.bulk.user.missing') })

            return
          }

          const thematicPageId = this.selectedThematicPage.id
          const certificateId = this.formData.id

          if (!certificateId) {
            this.setFeedback({ message: this.$t('management.certificate:customization.bulk.error') })

            return
          }

          const hasExistingCustomizations = this.customizations.some(c => c.thematicPageId === thematicPageId)

          if (hasExistingCustomizations) {
            try {
              await this.attemptDeleteCertificateCustomization({
                certificateId,
                thematicPageId
              })

              this.customizations = this.customizations.filter(c => c.thematicPageId !== thematicPageId)
            } catch (error) {
              console.error('Error deleting existing customizations before bulk save:', error)
              this.setFeedback({ message: this.$t('management.certificate:customization.delete.error') })

              return
            }
          }

          this.pendingRemoveIndividualCustomizations = false

          const payload = this.formatBulkCustomizationPayload(contents)

          await this.attemptSaveCertificateCustomizationBulk(payload)

          await this.refreshCertificateCustomizations()

          this.setFeedback({ message: this.$t('management.certificate:customization.bulk.success') })

          return
        }

        const payload = this.formatCustomizationPayload()

        if (this.isEditingCustomization && this.selectedCustomizationId) {
          await this.attemptUpdateCertificateCustomization({
            id: this.selectedCustomizationId,
            payload
          })

          await this.refreshCertificateCustomizations()

          this.setFeedback({ message: this.$t('management.certificate:customization.updated.success') })
        } else {
          await this.attemptSaveCertificateCustomization(payload)

          await this.refreshCertificateCustomizations()

          this.setFeedback({ message: this.$t('management.certificate:customization.created.success') })
        }
      } catch (error) {
        console.error('Error saving customization:', error)

        if (this.selectedContentType === 'thematic_page') {
          const contentsCount = this.selectedThematicPage ? this.getThematicPageContents(this.selectedThematicPage).length : 0

          console.error('Bulk payload context:', {
            thematicPageId: this.selectedThematicPage ? this.selectedThematicPage.id : null,
            items: contentsCount
          })
        } else {
          console.error('Payload:', this.formatCustomizationPayload())
        }

        const feedbackKey = this.selectedContentType === 'thematic_page'
          ? 'management.certificate:customization.bulk.error'
          : 'management.certificate:customization.error'

        this.setFeedback({ message: this.$t(feedbackKey) })
      } finally {
        this.setFetching(false)
      }
    },

    async deleteCustomization (thematicPage, content, contentType) {
      const isThematicPageDeletion = contentType === 'thematic_page'

      if (isThematicPageDeletion) {
        const hasCustomizations = this.customizations.some(c => c.thematicPageId === thematicPage.id)

        if (!hasCustomizations) return
      } else {
        const customization = this.getCustomization(thematicPage.id, content.id, contentType)

        if (!customization) return
      }

      this.setFetching(true)

      try {
        await this.attemptDeleteCertificateCustomization({
          certificateId: this.formData.id,
          thematicPageId: thematicPage.id
        })

        if (isThematicPageDeletion) {
          this.customizations = this.customizations.filter(c => c.thematicPageId !== thematicPage.id)
        } else {
          const index = this.customizations.findIndex(c =>
            c.thematicPageId === thematicPage.id &&
            c.contentId === content.id &&
            c.contentType === contentType
          )

          if (index !== -1) {
            this.customizations.splice(index, 1)
          }
        }

        this.setFeedback({ message: this.$t('management.certificate:customization.deleted.success') })
      } catch (error) {
        this.setFeedback({ message: this.$t('management.certificate:customization.delete.error') })
      } finally {
        this.setFetching(false)
      }
    }
  }

}
</script>

<template>
  <div class="main-content management-certificate-create">
    <ContentHeader
      :title="headerTitle"
      dark-theme
      sticky
      fixed
    >
      <Action
        slot="back"
        type="button"
        :text="$t('global:back.certificates')"
        class="btn-back"
        icon="keyboard_backspace"
        @click="leave()"
      />
      <ActionBar slot="actionbar" />
      <template slot="buttons">
        <Action
          v-if="!showCustomizationForm"
          :text="$t('global:form.save')"
          type="button"
          dark
          flat
          @click="submit(false)"
        />
      </template>
    </ContentHeader>

    <div class="main-content-inner">
      <div class="center">
        <div class="certificate-page-header">
          <h2 class="certificate-page-title">
            {{ $t('management.certificate.edit:page.title') }}
          </h2>
          <p class="certificate-page-description">
            {{ $t('management.certificate.edit:page.description') }}
          </p>
        </div>

        <!--
          Tabs - Always visible when editing
        -->
        <Tabs
          v-if="isEditing"
          :links="tabLinks"
          :alias-active="activeTab"
          use-alias
          class="certificate-tabs"
          @tabChange="onTabChange"
        />

        <!--
          Tab 1: Certificado Padrão
          - Aparece sempre quando está criando (não isEditing)
          - Ou quando está editando e a aba ativa é 'default'
        -->
        <div
          v-show="(!isEditing || activeTab === 'default') && !showCustomizationForm"
          class="tab-content certificate-form-container"
        >
          <form
            class="form"
            @submit.prevent="submit"
          >
            <InputField
              v-model="formData.name"
              :label="$t('global:form.management.certificate.model')"
              :validation="$v.formData.name"
              :counter="100"
            />

            <FormSection
              class="no-margin-bottom"
              :title="$t('global:form.management.certificate.background')"
            >
              <Radio
                v-model="certificateImagesType"
                :items="backAndFrontOptions"
                horizontal
              />
              <p class="form-hint">
                {{ $t('global:form.management.certificate.background.size') }}
              </p>
              <div class="row no-margin">
                <Upload
                  v-model="formData.imageFront"
                  class="col-6"
                  icon="image-multiple"
                  :label="$t('global:form.management.certificate.background.input')"
                  :description="$t('global:form.management.certificate.background.front')"
                  cropper
                  :width="2245"
                  :height="1587"
                  :preview="0.10"
                  :validation="$v.formData.imageFront"
                />
                <Upload
                  v-if="certificateImagesType === 'FV'"
                  v-model="formData.imageBack"
                  class="col-6"
                  icon="image-multiple"
                  :label="$t('global:form.management.certificate.background.input')"
                  :description="$t('global:form.management.certificate.background.back')"
                  cropper
                  :width="2245"
                  :height="1587"
                  :preview="0.10"
                  :validation="$v.formData.imageBack"
                />
              </div>
            </FormSection>

            <FormSection
              class="no-margin-top"
              :title="$t('global:form.management.certificate.tags')"
            >
              <p class="form-hint">
                {{ $t('global:form.management.certificate.tags.info') }}
              </p>
              <ul class="tags-list">
                <li
                  v-for="(item, index) in tagInfoList"
                  :key="index"
                  class="tag-item"
                  @click="addTagToHTML(item.value)"
                >
                  <span class="tag-item-value">{{ $t(`certificates.tags:${item.value}`) }}</span>
                </li>
              </ul>
            </FormSection>

            <FormSection :title="$t('certificate:text.editor')">
              <p class="form-hint">
                {{ $t('global:form.management.certificate.text.info') }}
              </p>
              <RichTextEditor
                v-model="formData.html"
                :validation="$v.formData.html"
              />
            </FormSection>

            <FormSection
              v-if="isEditing"
              class="form-section-center"
            >
              <Action
                type="button"
                primary
                fixed-width
                :text="$t('global:form.management.certificate.preview')"
                @click="preview"
              />
              <p
                v-if="formChanged"
                class="form-hint text-center"
              >
                {{ $t('global:form.management.certificate.preview.info') }}
              </p>
            </FormSection>
          </form>
        </div>

        <!--
          Tab 2: Páginas Temáticas Vinculadas - Lista
        -->
        <div
          v-show="activeTab === 'thematic' && isEditing && !showCustomizationForm"
          class="tab-content thematic-list-container"
        >
          <div class="thematic-pages-header">
            <h3>{{ $t('management.certificate:thematic.title') }}</h3>
            <p class="form-hint">
              {{ $t('management.certificate:thematic.description') }}
            </p>
          </div>

          <FilterList class="thematic-filter-list">
            <FilterListSearch
              slot="search"
              :hide-error-details="true"
              @search="searchThematicPages"
            />
          </FilterList>

          <div
            v-if="thematicPagesWithoutSolutions.length === 0 && thematicPagesWithSolutions.length === 0"
            class="no-thematic-pages"
          >
            <p>{{ $t('management.certificate:thematic.empty') }}</p>
          </div>

          <template v-else>
            <!--
              Cabeçalho das colunas
            -->
            <div class="thematic-list-header">
              <span class="header-name">{{ $t('management.certificate:column.name') }}</span>
              <span class="header-status">{{ $t('management.certificate:column.has.customization') }}</span>
              <span class="header-actions" />
            </div>

            <!--
              Lista de páginas temáticas SEM soluções (exibir como itens simples, sem accordion)
            -->
            <div
              v-if="thematicPagesWithoutSolutions.length > 0"
              class="thematic-pages-simple-list"
            >
              <div
                v-for="thematicPage in thematicPagesWithoutSolutions"
                :key="'simple-' + thematicPage.id"
                class="thematic-page-simple-item"
              >
                <span class="thematic-page-name">{{ thematicPage.name }}</span>
                <div class="thematic-page-status">
                  <span
                    :class="{ 'td-tag active-ribbon': isThematicPageConfigured(thematicPage.id), 'td-tag inactive-ribbon': !isThematicPageConfigured(thematicPage.id) }"
                  >
                    {{ isThematicPageConfigured(thematicPage.id) ? $t('global:yes') : $t('global:no') }}
                  </span>
                </div>
                <div class="thematic-page-actions td-actions">
                  <Dropdown
                    icon="dots-vertical"
                    right
                  >
                    <DropdownLink
                      v-if="isThematicPageConfigured(thematicPage.id)"
                      :text="$t('global:edit')"
                      @click="openCustomizationForm(thematicPage, thematicPage, 'thematic_page', true)"
                    />
                    <DropdownLink
                      v-else
                      :text="$t('management.certificate:thematic.configure.page')"
                      @click="handleConfigurePage(thematicPage)"
                    />
                    <DropdownLink
                      v-if="isThematicPageConfigured(thematicPage.id)"
                      :text="$t('global:remove')"
                      @click="confirmDeleteCustomization(thematicPage, thematicPage, 'thematic_page')"
                    />
                  </Dropdown>
                </div>
              </div>
            </div>

            <!--
            Lista de páginas temáticas COM soluções (exibir em accordion)
          -->
            <Accordion
              v-if="thematicPagesWithSolutions.length > 0"
              class="thematic-pages-accordion"
            >
              <AccordionItem
                v-for="thematicPage in thematicPagesWithSolutions"
                :key="thematicPage.id"
              >
                <template slot="header">
                  <div class="accordion-header-content">
                    <span class="accordion-title">{{ thematicPage.name }}</span>
                    <div class="accordion-header-status">
                      <span
                        :class="{ 'td-tag active-ribbon': isThematicPageConfigured(thematicPage.id), 'td-tag inactive-ribbon': !isThematicPageConfigured(thematicPage.id) }"
                      >
                        {{ isThematicPageConfigured(thematicPage.id) ? $t('global:yes') : $t('global:no') }}
                      </span>
                    </div>
                    <div
                      class="accordion-header-actions td-actions"
                      @click.stop
                    >
                      <Dropdown
                        icon="dots-vertical"
                        right
                      >
                        <DropdownLink
                          v-if="isThematicPageConfigured(thematicPage.id)"
                          :text="$t('global:edit')"
                          @click="openCustomizationForm(thematicPage, thematicPage, 'thematic_page', true)"
                        />
                        <DropdownLink
                          v-else
                          :text="$t('management.certificate:thematic.configure.page')"
                          @click="handleConfigurePage(thematicPage)"
                        />
                        <DropdownLink
                          v-if="isThematicPageConfigured(thematicPage.id)"
                          :text="$t('global:remove')"
                          @click="confirmDeleteCustomization(thematicPage, thematicPage, 'thematic_page')"
                        />
                      </Dropdown>
                    </div>
                  </div>
                </template>

                <template slot="content">
                  <div class="solutions-list">

                    <div
                      v-for="contentItem in getThematicPageContents(thematicPage)"
                      :key="`${contentItem.type}-${contentItem.id}`"
                      class="solution-item"
                      :class="{ 'is-disabled': isThematicPageConfigured(thematicPage.id) }"
                    >
                      <div class="solution-info">
                        <span class="solution-name">{{ contentItem.name }}</span>
                        <span class="solution-type-label">{{ contentItem.typeLabel }}</span>
                      </div>
                      <div class="solution-status">
                        <!--
                        Se a página temática está configurada via thematic_page, conteúdos mostram label especial
                      -->
                        <template v-if="isThematicPageConfigured(thematicPage.id)">
                          <span class="td-tag configured-by-page-ribbon">
                            {{ $t('management.certificate:thematic.configured.by.page') }}
                          </span>
                        </template>
                        <template v-else>
                          <span
                            :class="{ 'td-tag active-ribbon': isContentConfigured(thematicPage.id, contentItem.id, contentItem.type), 'td-tag inactive-ribbon': !isContentConfigured(thematicPage.id, contentItem.id, contentItem.type) }"
                          >
                            {{ isContentConfigured(thematicPage.id, contentItem.id, contentItem.type) ? $t('global:yes') : $t('global:no') }}
                          </span>
                        </template>
                      </div>
                      <div class="solution-actions td-actions">
                        <template v-if="!isThematicPageConfigured(thematicPage.id)">
                          <Dropdown
                            icon="dots-vertical"
                            right
                          >
                            <DropdownLink
                              v-if="isContentConfigured(thematicPage.id, contentItem.id, contentItem.type)"
                              :text="$t('global:edit')"
                              @click="openCustomizationForm(thematicPage, contentItem, contentItem.type, true)"
                            />
                            <DropdownLink
                              v-else
                              :text="$t('management.certificate:thematic.configure')"
                              @click="openCustomizationForm(thematicPage, contentItem, contentItem.type)"
                            />
                            <DropdownLink
                              v-if="isContentConfigured(thematicPage.id, contentItem.id, contentItem.type)"
                              :text="$t('global:remove')"
                              @click="confirmDeleteCustomization(thematicPage, contentItem, contentItem.type)"
                            />
                          </Dropdown>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
              </AccordionItem>
            </Accordion>

            <Pagination
              v-if="thematicPagesPagination.lastPage > 1"
              :active-page="thematicPagesPagination.page"
              :page-count="thematicPagesPagination.lastPage"
              @firstPage="firstThematicPage"
              @lastPage="lastThematicPage"
              @nextPage="nextThematicPage"
              @previousPage="prevThematicPage"
            />
          </template>
        </div>

        <!--
          Customization Form (inline view, not modal)
        -->
        <div
          v-if="showCustomizationForm"
          class="tab-content customization-form-container"
        >
          <div class="customization-header">
            <div class="customization-header-content">
              <div class="customization-header-text">
                <h3 class="customization-title">
                  {{ customizationModalTitle }}
                </h3>
                <p class="customization-subtitle">
                  {{ customizationSubtitle }}
                </p>
              </div>
              <Action
                type="button"
                flat
                :text="$t('global:back')"
                class="btn-back-customization"
                @click="closeCustomizationForm"
              />
            </div>
          </div>

          <form
            class="form customization-form"
            @submit.prevent="saveCustomization"
          >
            <InputField
              v-model="customizationFormData.name"
              :label="$t('management.certificate:field.certificate.name')"
              :placeholder="$t('management.certificate:field.certificate.name.placeholder')"
              :validation="$v.customizationFormData.name"
              :counter="100"
            />

            <FormSection
              class="no-margin-bottom"
              :title="$t('global:form.management.certificate.background')"
            >
              <Radio
                v-model="customizationImagesType"
                :items="backAndFrontOptions"
                horizontal
              />
              <p class="form-hint">
                {{ $t('global:form.management.certificate.background.size') }}
              </p>
              <div class="row no-margin">
                <Upload
                  v-model="customizationFormData.imageFront"
                  class="col-6"
                  icon="image-multiple"
                  :label="$t('global:form.management.certificate.background.input')"
                  :description="$t('global:form.management.certificate.background.front')"
                  cropper
                  :width="2245"
                  :height="1587"
                  :preview="0.10"
                  :validation="$v.customizationFormData.imageFront"
                />
                <Upload
                  v-if="customizationImagesType === 'FV'"
                  v-model="customizationFormData.imageBack"
                  class="col-6"
                  icon="image-multiple"
                  :label="$t('global:form.management.certificate.background.input')"
                  :description="$t('global:form.management.certificate.background.back')"
                  cropper
                  :width="2245"
                  :height="1587"
                  :preview="0.10"
                  :validation="$v.customizationFormData.imageBack"
                />
              </div>
            </FormSection>

            <FormSection
              class="no-margin-top"
              :title="$t('global:form.management.certificate.tags')"
            >
              <p class="form-hint">
                {{ $t('global:form.management.certificate.tags.info') }}
              </p>
              <ul class="tags-list">
                <li
                  v-for="(item, index) in tagInfoList"
                  :key="index"
                  class="tag-item"
                  @click="addTagToHTML(item.value, true)"
                >
                  <span class="tag-item-value">{{ $t(`certificates.tags:${item.value}`) }}</span>
                </li>
              </ul>
            </FormSection>

            <FormSection :title="$t('certificate:text.editor')">
              <p class="form-hint">
                {{ $t('global:form.management.certificate.text.info') }}
              </p>
              <RichTextEditor
                v-model="customizationFormData.html"
                :validation="$v.customizationFormData.html"
              />
            </FormSection>

            <div class="customization-form-footer">
              <Action
                type="button"
                flat
                :text="$t('global:cancel')"
                @click="closeCustomizationForm"
              />
              <Action
                type="button"
                primary
                :text="$t('management.certificate:customization.save')"
                @click="saveCustomization"
              />
            </div>
          </form>
        </div>
      </div>
    </div>

    <!--
      Modal de Preview (existente)
    -->
    <Modal
      class="management-certificate-modal"
      :active="modalActive"
      @close="modalActive = false"
    >
      <div class="modal-content">
        <iframe id="certificate_preview" />
      </div>
    </Modal>

    <!--
      Modal de confirmação de exclusão de personalização
    -->
    <ModalConfirm
      :active="showDeleteConfirmModal"
      :title="$t('management.certificate:delete.customization.title')"
      :description="$t('management.certificate:delete.customization.description')"
      @confirmAction="executeDeleteCustomization"
      @cancelAction="cancelDeleteCustomization"
    />

    <!--
      Modal de aviso ao configurar página com conteúdos personalizados
    -->
    <ModalConfirm
      :active="showPageConfigWarningModal"
      :title="$t('management.certificate:page.config.warning.title')"
      :description="$t('management.certificate:page.config.warning.description')"
      @confirmAction="confirmPageConfiguration"
      @cancelAction="cancelPageConfiguration"
    />
  </div>
</template>

<style src="@/assets/styles/themes/default/management.css"></style>

<style lang="scss" scoped>
// Estilos de tag de status (padrão do sistema)
.td-tag {
  display: inline-block;
  vertical-align: middle;
  font-size: 1.2em;
  color: rgba(0, 0, 0, 0.5);
  line-height: 30px;
  padding: 0 10px;
  min-width: 70px;
  text-align: center;
  background: #F3F4F7;
  border-radius: 50px;
  text-transform: none;

  &.active-ribbon {
    background: #beffbe;
  }

  &.inactive-ribbon {
    background: #ffadad;
  }

  &.configured-by-page-ribbon {
    background: #E8F4FD;
    color: #0047BB;
  }
}

.certificate-page-header {
  margin-bottom: 24px;

  .certificate-page-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #333;
  }

  .certificate-page-description {
    font-size: 14px;
    color: #666;
    margin: 0;
  }
}

.certificate-tabs {
  margin-bottom: 24px;
  text-align: center;
}

.tab-content {
  padding: 24px 0;
}

.certificate-form-container,
.thematic-list-container,
.customization-form-container {
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.form-hint {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #666;
}

.text-center {
  text-align: center;
}

// Estilo do dropdown para seguir o padrão do datatable
.td-actions {
  ::v-deep .dropdown-link {
    color: #3683A8;
  }
}

// Thematic Pages List
.thematic-pages-header {
  margin-bottom: 16px;

  h3 {
    font-family: Roboto, sans-serif;
    margin: 0 0 8px 0;
    font-size: 1.4rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.8);
  }
}

// Filter List
.thematic-filter-list {
  margin-bottom: 24px;

  ::v-deep .filter-options {
    float: none;
  }
}

// Cabeçalho da lista
.thematic-list-header {
  display: flex;
  align-items: center;
  padding: 12px 50px 12px 0;
  border-bottom: 2px solid #EAEAEA;

  .header-name {
    flex: 1;
    font-size: 1.6em;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.5);
    text-transform: uppercase;
    font-family: 'Quicksand';
  }

  .header-status {
    width: 200px;
    text-align: center;
    font-size: 1.6em;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.5);
    text-transform: uppercase;
    font-family: 'Quicksand';
  }

  .header-actions {
    width: 40px;
  }
}

// Lista simples para páginas sem soluções
.thematic-pages-simple-list {
  margin-bottom: 0;

  .thematic-page-simple-item {
    display: flex;
    align-items: center;
    padding: 12px 50px 12px 0px;
    border-bottom: 2px solid #EAEAEA;
    position: relative;

    .thematic-page-name {
      color: rgba(0, 0, 0, 0.7);
      font-weight: bold;
      flex: 1;
      display: inline-block;
      font-family: 'Quicksand';
      font-size: 1.6em;
    }

    .thematic-page-status {
      width: 150px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .thematic-page-actions {
      width: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  // Remover borda apenas se não houver accordion depois
  &:last-child .thematic-page-simple-item:last-child {
    border-bottom: none;
  }
}

.thematic-pages-accordion {
  ::v-deep .accordion-item {
    margin-bottom: 0;
    border-bottom: 2px solid #EAEAEA;

    &:last-child {
      border-bottom: none;
    }
  }

  ::v-deep .accordion-item-header {
    padding: 0;
  }

  // Animação do ícone do accordion - rotação suave 0° → 180°
  ::v-deep .accordion-item-header-icon {
    top: 50%;
    transform: translateY(-50%);
    transition: transform 300ms ease;
  }

  ::v-deep .accordion-item.is-open .accordion-item-header-icon {
    transform: translateY(-50%) rotate(180deg);
  }

  .accordion-header-content {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 12px 50px 12px 0px;

    .accordion-title {
      color: rgba(0, 0, 0, 0.7);
      font-weight: bold;
      flex: 1;
      display: inline-block;
      font-family: 'Quicksand';
      font-size: 1.6em;
    }

    .accordion-header-status {
      width: 150px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .accordion-header-actions {
      width: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

.solutions-list {
  padding: 0 16px 16px 20px;
  background: #fff;

  .solution-item {
    display: flex;
    align-items: center;
    padding: 12px 16px 12px 0;
    border-bottom: 1px solid #eee;

    &:last-child {
      border-bottom: none;
    }

    &.is-disabled {
      opacity: 0.6;

      .solution-name {
        color: rgba(0, 0, 0, 0.4);
      }
    }

    .solution-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex: 1;
    }

    .solution-name {
      color: rgba(0, 0, 0, 0.7);
      font-weight: bold;
      display: inline-block;
      font-family: 'Quicksand';
      font-size: 1.4em;
    }

    .solution-type-label {
      font-size: 11px;
      color: rgba(0, 0, 0, 0.5);
      text-transform: uppercase;
      font-family: 'Quicksand';
    }

    .solution-status {
      width: 150px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .solution-actions {
      width: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .no-solutions {
    padding: 16px 0;
    color: #999;
    font-style: italic;
    font-size: 14px;
  }
}

.no-thematic-pages {
  padding: 48px;
  text-align: center;
  color: #999;
  font-style: italic;
  background: #fafafa;
  border-radius: 8px;
}

// Customization Form
.customization-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;

  .customization-header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .customization-header-text {
    .customization-title {
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 4px 0;
      color: #333;
    }

    .customization-subtitle {
      font-size: 13px;
      color: #666;
      margin: 0;
    }
  }

  .btn-back-customization {
    flex-shrink: 0;
  }
}

.customization-form {
  .form-row {
    margin-bottom: 16px;
  }
}

.customization-form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}
</style>
